/**
 * Shared collaborator utilities for class-related components
 * Consolidates collaborator display logic across:
 * - ClassCard
 * - BrowseClassCard
 * - PhotoClassCard
 */

import type { CollaboratorProfileInfo } from "@/lib/api/classes";

/**
 * Get the list of profiles to display in the "with" badge
 *
 * For collaborative classes (shown on collaborator's profile):
 *   - Show owner profile + other collaborators
 *
 * For owned classes:
 *   - Show all collaborators
 *
 * @param isCollaborativeClass - Whether this class is shown on a collaborator's profile
 * @param ownerProfile - The owner's profile info (for collaborative classes)
 * @param collaborators - Array of collaborator profiles
 * @returns Array of profiles to display in the "with" badge
 */
export function getWithProfiles(
  isCollaborativeClass: boolean,
  ownerProfile: CollaboratorProfileInfo | undefined | null,
  collaborators: CollaboratorProfileInfo[] = []
): CollaboratorProfileInfo[] {
  if (isCollaborativeClass) {
    // Show owner + other collaborators
    return [...(ownerProfile ? [ownerProfile] : []), ...collaborators];
  }
  // Show all collaborators
  return collaborators;
}

/**
 * Check if the "with" badge should be shown
 *
 * @param isCollaborativeClass - Whether this class is shown on a collaborator's profile
 * @param ownerProfile - The owner's profile info (for collaborative classes)
 * @param collaborators - Array of collaborator profiles
 * @returns Whether to show the "with" badge
 */
export function shouldShowWithBadge(
  isCollaborativeClass: boolean,
  ownerProfile: CollaboratorProfileInfo | undefined | null,
  collaborators: CollaboratorProfileInfo[] = []
): boolean {
  return getWithProfiles(isCollaborativeClass, ownerProfile, collaborators).length > 0;
}

/**
 * Check if a class has collaborators
 *
 * @param collaborators - Array of collaborator profiles
 * @returns Whether the class has any collaborators
 */
export function hasCollaborators(
  collaborators: CollaboratorProfileInfo[] | undefined | null
): boolean {
  return !!collaborators && collaborators.length > 0;
}

/**
 * Get the display name for a collaborator
 *
 * @param profile - The collaborator profile
 * @param fallback - Fallback text if no name available
 * @returns The collaborator's display name
 */
export function getCollaboratorName(
  profile: CollaboratorProfileInfo,
  fallback = "Collaborator"
): string {
  return profile.full_name || profile.username || fallback;
}
